import { FormControl, Radio, RadioGroup } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { ChangeEvent, KeyboardEvent } from 'react';
import { Controller } from 'react-hook-form';

import { FormLabel } from 'core/elements/FormLabel';
import { IRadioChoice } from 'core/types';
import { toFieldFocusRef } from 'core/utils/fieldFocus';
import { Color } from 'theme/enums';

import { IProps as RadioGroupProps } from './RadioGroupInput';

interface IProps extends RadioGroupProps {
  onChange?: (event: ChangeEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>) => void;
  changeOnKeyDown?: boolean;
  labelSxProps?: SxProps;
}

const defaultRenderRadio = (
  choice: IRadioChoice,
  onChange?: (event: ChangeEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>) => void,
  changeOnKeyDown?: boolean,
  disabled?: boolean
) => (
  <Radio
    key={choice.value}
    value={choice.value}
    label={choice.label}
    onChange={onChange}
    onKeyDown={changeOnKeyDown ? onChange : undefined}
    disabled={disabled || choice.disabled}
  />
);

// Resolved from the group root, so it also covers radios rendered by a caller's renderRadio.
const getRadioToFocus = (group: HTMLElement) =>
  group.querySelector<HTMLInputElement>('input[type="radio"]:checked:not(:disabled)') ??
  group.querySelector<HTMLInputElement>('input[type="radio"]:not(:disabled)');

const RadioGroupInputWithControllerView = ({
  control,
  error,
  name,
  label,
  tooltip,
  choices = [],
  required,
  onChange,
  renderRadio = defaultRenderRadio,
  wrapperStyle,
  labelSize,
  labelSxProps,
  changeOnKeyDown = false,
  disabled,
  ...rest
}: IProps) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={rest?.defaultValue || null}
      render={({ field: { ref, ...field } }) => (
        <FormControl error={Boolean(error)} size={labelSize || rest?.size}>
          {label && (
            <FormLabel required={required} tooltip={tooltip} sx={labelSxProps}>
              {label}
            </FormLabel>
          )}
          <RadioGroup
            color={error ? Color.DANGER : Color.NEUTRAL}
            {...field}
            {...rest}
            ref={toFieldFocusRef(ref, getRadioToFocus)}
            name={name}
            sx={{ ...{ gap: '8px' }, ...wrapperStyle }}
          >
            {choices?.map((choice) => renderRadio(choice, onChange, changeOnKeyDown, disabled))}
          </RadioGroup>
        </FormControl>
      )}
    />
  );
};

export default RadioGroupInputWithControllerView;
